import { captureException } from '@sentry/react-router'
import { useEffect, type ReactElement } from 'react'
import { type ErrorResponse, isRouteErrorResponse, useParams, useRouteError } from 'react-router'
import { getErrorMessage } from '#app/utils/misc'
import { ErrorPage } from './error-page'
import { NotFoundPage } from './not-found-page'

type StatusHandler = (info: { error: ErrorResponse; params: Record<string, string | undefined> }) => ReactElement | null

export function GeneralErrorBoundary({
	defaultStatusHandler = ({ error }) => (
		<p>
			{error.status} {error.data}
		</p>
	),
	statusHandlers,
	unexpectedErrorHandler = error => <p>{getErrorMessage(error)}</p>,
}: {
	defaultStatusHandler?: StatusHandler
	statusHandlers?: Record<number, StatusHandler>
	unexpectedErrorHandler?: (error: unknown) => ReactElement | null
}) {
	const error = useRouteError()
	const params = useParams()
	const isResponse = isRouteErrorResponse(error)

	if (typeof document !== 'undefined') {
		console.error(error)
	}

	useEffect(() => {
		if (isResponse) return

		captureException(error)
	}, [error, isResponse])

  // @ts-ignore
  if (error.status === 404) {
    return <NotFoundPage />
  }

	return (
		<ErrorPage
			msg={
				isResponse
					? (statusHandlers?.[error.status] ?? defaultStatusHandler)({
							error,
							params,
					  })
					: unexpectedErrorHandler(error)
			}
		/>
	)
}
